import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Ban, Crosshair, Gamepad2, Loader2, MapPin, Menu, Navigation2, Play } from 'lucide-react';
import type { NavigationMapInfo, RobotPose, WaypointMap } from '@shared/lib/api/navigation';
import {
  EStopButton,
  IdleCountdown,
} from '@/components/layout/dashboard-middle';
import { ContactPopover } from '@/components/layout/dashboard-sidebar';
import { NavigationOverlay, type DrawMode } from '@/components/navigation/navigation-overlay';
import { CheckpointPanel } from '@/features/checkpoint/checkpoint-panel';
import EventLogPage from '@/features/event-log/event-log-page';
import { MobileHamburger } from '@/components/layout/mobile-hamburger';
import { useRobotStore } from '@/stores/useRobotStore';
import { useActionError } from '@/stores/useActionError';
import { cancelNavigation, startNavigation } from '@/lib/navigation-api';
import { startTaskExecution } from '@/lib/task-api';
import { useEffectiveOrgId } from '@/stores/useOrgStore';
import { ROUTES } from '@/lib/routes';

interface MobileShellProps {
  mapInfo: NavigationMapInfo | null;
  mapUrl: string;
  mapLoading: boolean;
  navPose: RobotPose | null;
  navPath: Array<[number, number]>;
  waypoints: WaypointMap;
}

function HamburgerButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      aria-label="Open menu"
      className="fixed top-3 left-3 z-[55] flex items-center justify-center w-11 h-11 rounded-full bg-surface border border-border shadow-md text-foreground hover:bg-surface-strong transition-colors"
    >
      <Menu size={22} />
    </button>
  );
}

function StartNavButton({ large }: { large?: boolean }) {
  const navAvailable = useRobotStore((s) => s.navAvailable);
  const state = useRobotStore((s) => s.stateMachineState);
  const runAction = useActionError((s) => s.runAction);
  const [pending, setPending] = useState(false);
  if (!navAvailable) return null;
  const disabled = pending || state === 'navigation';
  return (
    <button
      disabled={disabled}
      onClick={() => {
        setPending(true);
        runAction(startNavigation).finally(() => setPending(false));
      }}
      className={`w-full rounded-md bg-surface-strong border border-border font-semibold text-foreground hover:bg-surface-muted transition flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed ${
        large ? 'py-6 text-lg' : 'py-3 text-base'
      }`}
    >
      <Navigation2 size={large ? 22 : 16} />
      {pending ? 'Starting…' : 'Start Navigation'}
    </button>
  );
}

function StartTaskButton({ large }: { large?: boolean }) {
  const state = useRobotStore((s) => s.stateMachineState);
  const runAction = useActionError((s) => s.runAction);
  const [pending, setPending] = useState(false);
  const disabled = pending || state === 'task_execution';
  return (
    <button
      disabled={disabled}
      onClick={() => {
        setPending(true);
        runAction(startTaskExecution).finally(() => setPending(false));
      }}
      className={`w-full rounded-md bg-surface-strong border border-border font-semibold text-foreground hover:bg-surface-muted transition flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed ${
        large ? 'py-6 text-lg' : 'py-3 text-base'
      }`}
    >
      <Play size={large ? 22 : 16} />
      {pending ? 'Starting…' : 'Start Task'}
    </button>
  );
}

function VerneControlBlock() {
  return (
    <div className="rounded-md border border-border bg-surface/40 p-4 flex flex-col gap-3">
      <span className="text-[11px] uppercase tracking-wider text-muted">Verne Control</span>
      <p className="text-sm text-muted leading-relaxed">
        An operator at Verne is controlling the robot remotely. Feel free to contact the
        operator using the button below for questions or concerns.
      </p>
      <ContactPopover
        trigger={({ onClick }) => (
          <button
            onClick={onClick}
            className="w-full rounded-md border border-border bg-surface-muted px-4 py-3 text-base font-semibold text-muted hover:bg-surface-strong transition"
          >
            Contact Operator
          </button>
        )}
      />
    </div>
  );
}

function ErrorBlock() {
  return (
    <div className="rounded-md border border-danger/40 bg-danger/10 p-4 flex flex-col gap-2">
      <span className="text-sm font-semibold text-danger">Robot Error</span>
      <p className="text-sm text-foreground leading-relaxed">
        The robot encountered an error. Please contact Verne support for assistance.
      </p>
    </div>
  );
}

function HealingBlock() {
  return (
    <div className="rounded-md border border-orange-500/40 bg-orange-500/10 p-4 flex flex-col gap-2">
      <span className="text-sm font-semibold text-orange-500">Issue Detected</span>
      <p className="text-sm text-foreground leading-relaxed">
        An issue with the system has been detected, attempting to fix it.
      </p>
    </div>
  );
}

function NavActionGrid({
  drawMode,
  onDrawModeChange,
  onOpenJoystick,
}: {
  drawMode: DrawMode | null;
  onDrawModeChange: (mode: DrawMode | null) => void;
  onOpenJoystick: () => void;
}) {
  const navStatus = useRobotStore((s) => s.navStatus);
  const runAction = useActionError((s) => s.runAction);
  const cancelDisabled = navStatus !== 'active';
  const toggle = (mode: DrawMode) => onDrawModeChange(drawMode === mode ? null : mode);

  const base =
    'w-full rounded-md border-2 px-3 py-3 text-sm font-semibold transition flex items-center justify-center gap-2';

  return (
    <div className="grid grid-cols-2 gap-2 w-full max-w-md mx-auto">
      <button
        disabled={cancelDisabled}
        onClick={() => runAction(cancelNavigation)}
        className={`${base} border-danger/40 text-danger bg-danger/10 enabled:hover:bg-danger/20 disabled:opacity-40 disabled:cursor-not-allowed`}
      >
        <Ban size={16} />
        Cancel Waypoint
      </button>
      <button
        onClick={() => toggle('pose')}
        className={`${base} ${
          drawMode === 'pose'
            ? 'bg-orange-500 border-orange-400 text-white'
            : 'border-border bg-surface/40 text-foreground hover:bg-surface-strong'
        }`}
      >
        <Crosshair size={16} />
        Set Current Position
      </button>
      <button
        onClick={() => toggle('goal')}
        className={`${base} ${
          drawMode === 'goal'
            ? 'bg-accent border-accent text-white'
            : 'border-border bg-surface/40 text-foreground hover:bg-surface-strong'
        }`}
      >
        <MapPin size={16} />
        Set Custom Waypoint
      </button>
      <button
        onClick={onOpenJoystick}
        className={`${base} border-border bg-surface/40 text-foreground hover:bg-surface-strong`}
      >
        <Gamepad2 size={16} />
        Joystick Control
      </button>
    </div>
  );
}

function MobileNavigationView({
  mapInfo,
  mapUrl,
  mapLoading,
  navPose,
  navPath,
  waypoints,
}: MobileShellProps) {
  const navAvailable = useRobotStore((s) => s.navAvailable);
  const navigate = useNavigate();
  const [drawMode, setDrawMode] = useState<DrawMode | null>(null);

  return (
    <div className="h-full w-full flex flex-col overflow-hidden">
      <div className="flex-1 min-h-0 relative">
        {mapInfo && navAvailable ? (
          <NavigationOverlay
            compact
            mapInfo={mapInfo}
            mapUrl={mapUrl}
            robotPose={navPose}
            path={navPath}
            waypoints={waypoints}
            drawMode={drawMode}
            onDrawModeChange={setDrawMode}
          />
        ) : mapLoading ? (
          <div className="h-full w-full flex items-center justify-center">
            <Loader2 size={64} className="animate-spin text-accent" />
          </div>
        ) : (
          <div className="h-full w-full flex items-center justify-center text-sm text-muted">
            Map unavailable
          </div>
        )}
      </div>

      <div className="shrink-0 px-3 pt-2 pb-2">
        <NavActionGrid
          drawMode={drawMode}
          onDrawModeChange={setDrawMode}
          onOpenJoystick={() => navigate(ROUTES.JOYSTICK)}
        />
      </div>

      <div className="shrink-0 px-4 pb-4 relative z-[80] w-full max-w-md mx-auto">
        <EStopButton />
      </div>
    </div>
  );
}

function MobileDashboard(props: MobileShellProps) {
  const state = useRobotStore((s) => s.stateMachineState);
  const healingInProgress = useRobotStore((s) => s.healingInProgress);

  const isIdle = state === 'idle' || state === 'booting' || state == null;
  const inNavigation = state === 'navigation';
  const inTask = state === 'task_execution';
  const inVerne = state === 'verne_control';
  const isError = state?.toLowerCase() === 'error';
  const isHealing = isError && healingInProgress;

  // Navigation state owns its own layout: fullscreen map + action grid + e-stop.
  if (inNavigation) {
    return <MobileNavigationView {...props} />;
  }

  return (
    <div className="h-full w-full flex flex-col overflow-hidden">
      <div className="flex-1 min-h-0 overflow-y-auto pt-16 px-4 pb-4 flex flex-col items-center gap-4">
        {isIdle && (
          <div className="w-full max-w-md flex flex-col gap-3 mt-4">
            <StartNavButton large />
            <StartTaskButton large />
          </div>
        )}

        {inTask && (
          <div className="w-full max-w-md flex flex-col gap-3">
            <IdleCountdown />
            <CheckpointPanel />
          </div>
        )}

        {inVerne && (
          <div className="w-full max-w-md">
            <VerneControlBlock />
          </div>
        )}

        {isError && (
          <div className="w-full max-w-md">
            {isHealing ? <HealingBlock /> : <ErrorBlock />}
          </div>
        )}
      </div>

      <div className="shrink-0 px-4 pb-4 flex flex-col gap-2 bg-background">
        {inTask && (
          <div className="w-full max-w-md mx-auto">
            <StartNavButton />
          </div>
        )}
        <div className="relative z-[80] w-full max-w-md mx-auto">
          <EStopButton />
        </div>
      </div>
    </div>
  );
}

export function MobileShell(props: MobileShellProps) {
  const pathname = useLocation().pathname;
  const isLoggedIn = useEffectiveOrgId() != null;
  const onEventLog = isLoggedIn && pathname === ROUTES.EVENT_LOG;

  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  return (
    <div className="relative h-full w-full overflow-hidden">
      <HamburgerButton onClick={() => setMenuOpen(true)} />

      {onEventLog ? (
        <div className="h-full w-full overflow-y-auto pt-16">
          <EventLogPage />
        </div>
      ) : (
        <MobileDashboard {...props} />
      )}

      {/* Always-mounted sliding drawer. When closed it sits offscreen-left
          with pointer-events disabled so the shell underneath stays
          interactive. z-90 covers the shell's z-80 e-stop while open. */}
      <div
        aria-hidden={!menuOpen}
        className={`fixed inset-0 z-[90] transition-transform duration-300 ease-out ${
          menuOpen ? 'translate-x-0' : '-translate-x-full pointer-events-none'
        }`}
      >
        <MobileHamburger onClose={() => setMenuOpen(false)} />
      </div>
    </div>
  );
}
